import {
  AfterViewInit, Component, computed, ElementRef, HostBinding, Inject, OnChanges, OnDestroy, Renderer2, Signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { RenderEvent } from 'render/render-events/render-events.model';
import { RenderEventsService } from 'render/render-events/render-events.service';
import { IdtSharedRenderService } from 'render/render-shared/idt-shared-render.service';
import { getBooleanParam } from 'render/render-shared/ui/render-product-prices/render-product-prices-utils';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { GrowthbookConfig, RenderGrowthbookConfigsService } from 'render/services/render-growthbook-configs/render-growthbook-configs.service';
import { Observable } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { IdtImage } from 'shared/idt-components/idt-components-cms.model';
import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';
import { AmandaOptions, AssetsSharedService } from 'shared/services/assets-shared.service';
import { toNumber } from 'shared/utils/data-utils';

import { PieceMaxiLogoSettings } from 'extensions/st/piece-maxi-logo/front/models/piece-maxi-logo.models';


@Component({
  selector: 'piece-maxi-logo-render',
  templateUrl: './piece-maxi-logo-render.component.html',
  styleUrls: ['./piece-maxi-logo-render.component.scss']
})
export class PieceMaxiLogoRenderComponent
  extends BaseRenderComponent<PieceMaxiLogoSettings>
  implements AfterViewInit, OnChanges, OnDestroy {

  @HostBinding('style.z-index') public zIndex = '1';
  @HostBinding('class.fade-enabled') public fadeEnabled = false;
  @HostBinding('class.is-visible') public isVisible = false;

  @HostBinding('style.display')
  public get hostDisplay(): string | null {
    return this.isWorkspaceMode ? 'none' : null;
  }

  public orientation$!: Observable<string>;

  public readonly isMaxiLogoEnabled = computed(() => {
    return getBooleanParam(this.configs(), 'content.home-maxilogo.enable', true);
  });

  private static readonly hiddenStateDataAttribute = 'maxiLogoHidden';
  private static readonly visibilityShowThreshold = 0.52;
  private static readonly visibilityHideThreshold = 0.48;
  private static readonly scrollDirectionDeltaThreshold = 4;
  private static readonly topSafeZoneThreshold = 24;

  private scrollBound?: () => void;
  private hasViewInitialized = false;
  private hasAppliedInitialParallax = false;
  private scrollFrameId?: number;
  private lastScrollPosition = 0;
  private scrollDirection: 'up' | 'down' | null = null;
  private sliderId: string | null = null;
  private hasSliderControl = false;
  private isActiveSliderLogo = true;
  private stackedSliceLayer: HTMLElement | null = null;
  private priorityLayer: HTMLElement | null = null;

  private readonly configs: Signal<GrowthbookConfig[]> = toSignal(
    this.renderGrowthbookService.configurations$,
    { initialValue: [] as GrowthbookConfig[] }
  );

  constructor(
    @Inject('Window') private readonly window: Window,
    private readonly idtSharedRenderService: IdtSharedRenderService,
    private readonly renderConfigurationService: RenderConfigurationService,
    private readonly renderGrowthbookService: RenderGrowthbookConfigsService,
    private readonly renderEventsService: RenderEventsService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly elRef: ElementRef,
    private readonly renderer: Renderer2
  ) {
    super();
    this.orientation$ = this.idtSharedRenderService.screenOrientation$;
  }

  public ngAfterViewInit(): void {
    this.hasViewInitialized = true;

    this.renderGrowthbookService.configurations$.pipe(
      startWith([]),
      takeUntil(this.clearSubscriptions$)
    ).subscribe(() => {
      if (!this.isMaxiLogoEnabled()) {
        return;
      }

      if (this.isWorkspaceMode) {
        return;
      }

      this.capturePriorityLayer();
      this.applyStackedLayerTransformFix();

      if (this.settings.general.isInsideSliceSlider) {
        this.sliderId = this.getSliderIdInSection();
        // Assume slide 0 active until the slider confirms it, avoiding a flash of stacked logos.
        this.hasSliderControl = true;
        this.isActiveSliderLogo = this.getLogoIndexInSection() === 0;
        this.setupSliderSync();
      } else {
        this.hasSliderControl = false;
        this.isActiveSliderLogo = true;
      }
      this.updateVisibilityObserver();
      this.setupScrollParallax();
    });
  }

  public get hasToApplyAkamaiOptimization(): boolean {
    return this.settings.styles.objectFit === 'cover' && this.slot?.height === '100vh' && this.slot?.width === '100%';
  }

  public ngOnChanges() {
    if (!this.isMaxiLogoEnabled()) {
      return;
    }
    const objectFit = this.settings.styles.objectFit;
    const objectPosition = this.settings.styles.objectPosition;
    this.zIndex = `${this.settings.styles.zIndex ?? 1}`;
    this.fadeEnabled = !!this.settings.general.enableFadeEffect;

    if (!!objectFit) {
      this.renderer.setStyle(this.elRef.nativeElement, '--imageObjectFit', `${objectFit}`, 2);
    }

    if (!!objectPosition) {
      this.renderer.setStyle(this.elRef.nativeElement, '--imageObjectPosition', `${objectPosition}`, 2);
    }

    if (this.hasViewInitialized) {
      this.updateVisibilityObserver();
    } else {
      this.isVisible = true;
    }
  }

  public ngOnDestroy(): void {
    this.removeScrollParallax();
    this.clearStackedLayerTransformFix();
    super.ngOnDestroy();
  }

  public getPortraitImageSrc(): LazyDataInterface {
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const image = this.settings.general.portraitImage?.image;
    const akamaiOptions: AmandaOptions = {
      isWidthParamEnabled: this.renderConfigurationService.config.amandaImageVideoManagerEnabled
    };

    if (this.hasToApplyAkamaiOptimization) {
      akamaiOptions.width = this.applyAkamaiOptimization(this.settings.general.portraitImage);
    } else {
      akamaiOptions.width = this.assetsSharedService.getWidth(this.elRef);
    }

    return {
      src: this.assetsSharedService.getAssetUrl(image!, assetsUrl, akamaiOptions),
      height: toNumber(this.settings.general.portraitImage?.height),
      width: toNumber(this.settings.general.portraitImage?.width)
    };
  }

  public getLandscapeImageSrc(): LazyDataInterface {
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const image = this.settings.general.landscapeImage?.image;
    const akamaiOptions: AmandaOptions = {
      isWidthParamEnabled: this.renderConfigurationService.config.amandaImageVideoManagerEnabled
    };

    if (this.hasToApplyAkamaiOptimization) {
      akamaiOptions.width = this.applyAkamaiOptimization(this.settings.general.landscapeImage);
    } else {
      akamaiOptions.width = this.assetsSharedService.getWidth(this.elRef);
    }

    return {
      src: this.assetsSharedService.getAssetUrl(image!, assetsUrl, akamaiOptions),
      width: toNumber(this.settings.general.landscapeImage?.width),
      height: toNumber(this.settings.general.landscapeImage?.height)
    };
  }

  private updateVisibilityObserver(): void {
    this.isVisible = false;
    this.window.requestAnimationFrame(() => {
      this.isVisible = true;
    });
  }

  private setupScrollParallax(): void {
    this.removeScrollParallax();
    this.lastScrollPosition = this.getCurrentScrollPosition();
    this.scrollBound = () => {
      if (this.scrollFrameId !== undefined) {
        return;
      }
      this.scrollFrameId = this.window.requestAnimationFrame(() => {
        this.detectScrollDirection();
        this.applyParallax();
        this.scrollFrameId = undefined;
      });
    };
    this.window.addEventListener('scroll', this.scrollBound, { passive: true });
    this.applyParallax();
    this.hasAppliedInitialParallax = true;
  }

  private applyParallax(): void {
    if (this.isWorkspaceMode) {
      return;
    }

    const el = this.elRef.nativeElement as HTMLElement;
    const parent = el.parentElement;
    if (!parent) {
      return;
    }

    const visibilityTarget = this.getVisibilityTarget(el);
    if (!visibilityTarget) {
      return;
    }
    const wasSectionVisible = el.dataset[PieceMaxiLogoRenderComponent.hiddenStateDataAttribute] !== 'true';
    const isSectionVisible = this.isVisibleInViewportWithHysteresis(visibilityTarget, wasSectionVisible);


    if (this.settings.general.isInsideSliceSlider && this.hasSliderControl && !this.isActiveSliderLogo) {
      this.setElementVisibility(el, false, !this.hasAppliedInitialParallax);
      el.dataset[PieceMaxiLogoRenderComponent.hiddenStateDataAttribute] = 'true';
      this.updateLayerPriority(false);
      return;
    }

    const previousMaxiLogo = this.getAdjacentMaxiLogo(-1);
    const previousVisibilityTarget = previousMaxiLogo ? this.getVisibilityTarget(previousMaxiLogo) : undefined;
    const wasPreviousVisible = previousMaxiLogo?.dataset[PieceMaxiLogoRenderComponent.hiddenStateDataAttribute] !== 'true';
    const isPreviousVisible = !!previousVisibilityTarget
      ? this.isVisibleInViewportWithHysteresis(previousVisibilityTarget, wasPreviousVisible)
      : false;
    const canBeVisible = this.settings.general.isInsideSliceSlider
      ? true
      : (!previousMaxiLogo || !isPreviousVisible);
    const shouldHideByScrollDirection = this.shouldHideByScrollDirection();
    const isVisibleBySlider = !this.settings.general.isInsideSliceSlider
      || !this.hasSliderControl
      || this.isActiveSliderLogo;
    const shouldShow = canBeVisible && isSectionVisible && isVisibleBySlider && !shouldHideByScrollDirection;

    if (!shouldShow) {
      this.setElementVisibility(el, false, !this.hasAppliedInitialParallax);
      const isHiddenForSequence = !isSectionVisible
        || !canBeVisible
        || !isVisibleBySlider
        || shouldHideByScrollDirection;
      el.dataset[PieceMaxiLogoRenderComponent.hiddenStateDataAttribute] = `${isHiddenForSequence}`;
      this.updateLayerPriority(false);
      return;
    }

    this.setElementVisibility(el, true);
    el.dataset[PieceMaxiLogoRenderComponent.hiddenStateDataAttribute] = 'false';
    this.updateLayerPriority(true);
  }

  private setElementVisibility(element: HTMLElement, isVisible: boolean, suppressTransition = false): void {
    if (isVisible) {
      this.renderer.removeStyle(element, 'opacity');
      return;
    }

    if (suppressTransition) {
      this.renderer.setStyle(element, 'transition', 'none');
      this.renderer.setStyle(element, 'opacity', '0');
      element.getBoundingClientRect();
      this.window.requestAnimationFrame(() => this.renderer.removeStyle(element, 'transition'));
      return;
    }

    this.renderer.setStyle(element, 'opacity', '0');
  }

  private setupSliderSync(): void {
    this.renderEventsService.events
      .pipe(takeUntil(this.clearSubscriptions$))
      .subscribe((renderEvent: RenderEvent) => {
        const eventType = renderEvent.extra?.type;
        if (eventType !== 'sliceSliderChangeSlide') {
          return;
        }

        const eventData = renderEvent.data as { componentId?: string; currentSlide?: number };
        if (!eventData?.componentId) {
          return;
        }

        if (!!this.sliderId && eventData.componentId !== this.sliderId) {
          return;
        }

        const currentSlide = Number(eventData.currentSlide);
        if (!Number.isFinite(currentSlide) || currentSlide < 1) {
          return;
        }

        const logoIndex = this.getLogoIndexInSection();
        if (logoIndex < 0) {
          return;
        }

        this.hasSliderControl = true;
        this.isActiveSliderLogo = logoIndex === currentSlide - 1;
        this.applyParallax();
      });
  }

  private detectScrollDirection(): void {
    const currentScrollPosition = this.getCurrentScrollPosition();
    const delta = currentScrollPosition - this.lastScrollPosition;
    const isNearTop = this.isNearTop();

    if (isNearTop && Math.abs(delta) < PieceMaxiLogoRenderComponent.scrollDirectionDeltaThreshold) {
      this.scrollDirection = null;
    } else if (delta > 0) {
      this.scrollDirection = 'down';
    } else if (delta < 0) {
      this.scrollDirection = 'up';
    } else {
      this.scrollDirection = null;
    }

    this.lastScrollPosition = currentScrollPosition;
  }

  private shouldHideByScrollDirection(): boolean {
    if (!this.fadeEnabled) {
      return false;
    }

    if (this.scrollDirection !== 'down') {
      return false;
    }

    return !this.isNearTop();
  }

  private isNearTop(): boolean {
    return this.getCurrentScrollPosition() <= PieceMaxiLogoRenderComponent.topSafeZoneThreshold;
  }

  private getCurrentScrollPosition(): number {
    return Number(this.window.pageYOffset ?? this.window.scrollY ?? 0);
  }

  private isHalfVisibleInViewport(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    const viewportHeight = this.window.innerHeight;
    const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));

    return visibleHeight >= rect.height * 0.5;
  }

  private isVisibleInViewportWithHysteresis(element: HTMLElement, wasVisible: boolean): boolean {
    const rect = element.getBoundingClientRect();
    const viewportHeight = this.window.innerHeight;
    const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
    const threshold = wasVisible
      ? PieceMaxiLogoRenderComponent.visibilityHideThreshold
      : PieceMaxiLogoRenderComponent.visibilityShowThreshold;

    return visibleHeight >= rect.height * threshold;
  }

  private removeScrollParallax(): void {
    if (this.scrollFrameId !== undefined) {
      this.window.cancelAnimationFrame(this.scrollFrameId);
      this.scrollFrameId = undefined;
    }
    if (this.scrollBound) {
      this.window.removeEventListener('scroll', this.scrollBound);
      this.scrollBound = undefined;
    }
  }

  private getAdjacentMaxiLogo(offset: number): HTMLElement | undefined {
    const host = this.elRef.nativeElement as HTMLElement;
    const rootNode = host.getRootNode();

    if (!(rootNode instanceof ShadowRoot) && !(rootNode instanceof Document)) {
      return undefined;
    }

    const allMaxiLogos = Array.from(rootNode.querySelectorAll('piece-maxi-logo-render'));
    const currentIndex = allMaxiLogos.indexOf(host);

    if (currentIndex < 0) {
      return undefined;
    }

    const adjacentElement = allMaxiLogos[currentIndex + offset];
    return adjacentElement instanceof HTMLElement ? adjacentElement : undefined;
  }

  private getVisibilityTarget(hostElement: HTMLElement): HTMLElement | null {
    const stackedContainer = hostElement.closest('slice-stacked-render');
    if (stackedContainer instanceof HTMLElement) {
      return stackedContainer;
    }

    const section = hostElement.closest('render-slice');
    if (section instanceof HTMLElement) {
      return section;
    }

    return hostElement.parentElement;
  }

  private applyStackedLayerTransformFix(): void {
    const host = this.elRef.nativeElement as HTMLElement;
    const stackedContainer = host.closest('slice-stacked-render');
    if (!(stackedContainer instanceof HTMLElement)) {
      return;
    }

    const stackedLayer = host.closest('render-slice.slice-stacked');
    if (!(stackedLayer instanceof HTMLElement)) {
      return;
    }

    this.stackedSliceLayer = stackedLayer;
    this.renderer.setStyle(stackedLayer, 'transform', 'none');
  }

  private capturePriorityLayer(): void {
    const host = this.elRef.nativeElement as HTMLElement;

    this.priorityLayer = host.closest('render-slice.slice-stacked')
      ?? host.closest('render-slice-slot')
      ?? host.closest('render-slice');
  }

  private clearStackedLayerTransformFix(): void {
    if (this.priorityLayer) {
      this.renderer.removeStyle(this.priorityLayer, 'z-index');
      this.priorityLayer = null;
    }

    if (!this.stackedSliceLayer) {
      return;
    }

    this.renderer.removeStyle(this.stackedSliceLayer, 'transform');
    this.stackedSliceLayer = null;
  }

  private updateLayerPriority(logoVisible: boolean): void {
    if (!this.priorityLayer) {
      return;
    }

    if (logoVisible) {
      this.renderer.setStyle(this.priorityLayer, 'z-index', '6');
      return;
    }

    this.renderer.removeStyle(this.priorityLayer, 'z-index');
  }

  private get isWorkspaceMode(): boolean {
    return this.type === 'workspace';
  }

  private getSliderIdInSection(): string | null {
    const host = this.elRef.nativeElement as HTMLElement;
    const section = host.closest('render-slice');
    if (!section) {
      return null;
    }

    const sliderElement = section.querySelector('slice-slider-render');
    if (!sliderElement) {
      return null;
    }

    const sliderSection = sliderElement.closest('render-slice[data-id]');
    return sliderSection?.getAttribute('data-id') ?? null;
  }

  private getLogoIndexInSection(): number {
    const host = this.elRef.nativeElement as HTMLElement;
    const rootNode = host.getRootNode();
    if (!(rootNode instanceof ShadowRoot) && !(rootNode instanceof Document)) {
      return -1;
    }

    const logos = Array.from(rootNode.querySelectorAll('piece-maxi-logo-render'));
    return logos.indexOf(host);
  }

  private applyAkamaiOptimization(image: IdtImage | null | undefined): number {
    return this.assetsSharedService.getOptimumImageWidth(
      toNumber(image?.height)!,
      toNumber(image?.width)!,
      this.window.innerHeight,
      this.assetsSharedService.getWidth(this.elRef)
    );
  }

}
